import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { appVersion } from '../../../environments/version';

@Injectable({
  providedIn: 'root'
})
export class VersionUpdateService {
  private swUpdate = inject(SwUpdate);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private checkInterval = 5 * 60 * 1000; // Verificar a cada 5 minutos
  private updateInProgress = false;
  private lastKnownServerVersion: string | null = null;
  private readonly reloadAttemptKey = 'update-reload-attempted-for-version';
  private readonly hardReloadGuardKey = 'hard-reload-last-ts';
  private readonly hardReloadCountKey = 'hard-reload-count';
  private readonly autoReloadVersionKey = 'sw-auto-reload-version';
  private readonly minReloadIntervalMs = 15000;
  private readonly maxReloadsPerTab = 2;

  constructor() {
    if (this.isBrowser) {
      this.initializeUpdateChecks();
    }
  }

  private initializeUpdateChecks() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => {
          if (event.type === 'VERSION_READY') {
            // Atualização baixada e pronta para ativar: aplicar automaticamente.
            void this.forceUpdate();
          }

          if (event.type === 'VERSION_INSTALLATION_FAILED') {
            console.error('Falha ao instalar atualização do Service Worker:', event);
          }
        });

      interval(this.checkInterval)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          void this.checkForUpdates();
        });

      void this.checkForUpdates();
    } else {
      interval(this.checkInterval)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          void this.checkVersionWithoutSW();
        });

      void this.checkVersionWithoutSW();
    }
  }

  private async checkForUpdates(): Promise<boolean> {
    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      // Não forçar update aqui para evitar loops de reload em browsers mais sensíveis.
      // A atualização automática é feita apenas ao receber VERSION_READY.
      return updateFound;
    } catch (err) {
      console.error('Erro ao verificar atualizações:', err);
      return false;
    }
  }

  private async checkVersionWithoutSW() {
    // Fallback sem SW: comparar versão embutida (bundle) vs versão publicada no servidor.
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        headers: {
          'cache-control': 'no-cache'
        }
      });

      if (!response.ok) return;

      const versionData = await response.json();
      const serverVersion = String(versionData?.version ?? '').trim();
      const currentVersion = String(appVersion).trim();

      if (!serverVersion) return;

      // Evita ficar emitindo repetidamente a mesma versão
      if (this.lastKnownServerVersion === serverVersion) return;
      this.lastKnownServerVersion = serverVersion;

      if (serverVersion !== currentVersion) {
        // Sem SW, a melhor aproximação é tentar recarregar para buscar os novos bundles.
        // Evita loop infinito: tenta só uma vez por versão do servidor.
        const attemptedFor = localStorage.getItem(this.reloadAttemptKey);
        if (attemptedFor !== serverVersion) {
          localStorage.setItem(this.reloadAttemptKey, serverVersion);
          this.forceHardReload();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar version.json:', error);
    }
  }

  /**
   * Força a atualização da aplicação.
   *
   * Importante: não limpa localStorage/sessionStorage nem desregistra o SW,
   * para evitar logout e loops. O fluxo recomendado é ativar e recarregar.
   */
  async forceUpdate(): Promise<void> {
    if (!this.isBrowser) return;
    if (this.updateInProgress) return;
    if (!this.canAutoReloadForCurrentVersion()) return;

    this.updateInProgress = true;
    try {
      if (this.swUpdate.isEnabled) {
        await this.swUpdate.activateUpdate();
        this.forceHardReload();
      } else {
        this.forceHardReload();
      }
    } catch (error) {
      console.error('Erro ao forçar atualização:', error);
      this.forceHardReload();
    } finally {
      this.updateInProgress = false;
    }
  }

  /**
   * Força um reload completo da página, ignorando cache do navegador
   */
  private forceHardReload(): void {
    if (!this.canReloadNow()) {
      return;
    }
    window.location.reload();
  }

  private canReloadNow(): boolean {
    const currentUrl = new URL(window.location.href);
    const hasReloadParam = currentUrl.searchParams.has('_reload');
    const hasAuthCallbackParams = currentUrl.searchParams.has('code') && currentUrl.searchParams.has('state');
    if (hasReloadParam || hasAuthCallbackParams) {
      console.warn('Reload automático bloqueado em URL sensível para evitar loop.');
      return false;
    }

    const reloadCountRaw = sessionStorage.getItem(this.hardReloadCountKey);
    const reloadCount = reloadCountRaw ? Number(reloadCountRaw) : 0;
    if (Number.isFinite(reloadCount) && reloadCount >= this.maxReloadsPerTab) {
      console.warn('Reload automático bloqueado por limite de tentativas na aba.');
      return false;
    }

    const now = Date.now();
    const lastReloadRaw = sessionStorage.getItem(this.hardReloadGuardKey);
    const lastReload = lastReloadRaw ? Number(lastReloadRaw) : 0;

    if (Number.isFinite(lastReload) && now - lastReload < this.minReloadIntervalMs) {
      console.warn('Reload bloqueado para evitar loop de atualização.');
      return false;
    }

    sessionStorage.setItem(this.hardReloadGuardKey, now.toString());
    sessionStorage.setItem(this.hardReloadCountKey, String((Number.isFinite(reloadCount) ? reloadCount : 0) + 1));
    return true;
  }

  private canAutoReloadForCurrentVersion(): boolean {
    const currentVersion = String(appVersion).trim();
    if (!currentVersion) {
      return true;
    }

    const alreadyReloadedForVersion = localStorage.getItem(this.autoReloadVersionKey);
    if (alreadyReloadedForVersion === currentVersion) {
      console.warn('Reload automático já executado para esta versão.');
      return false;
    }

    localStorage.setItem(this.autoReloadVersionKey, currentVersion);
    return true;
  }

}